import { moneyExactSigned } from "../../lib/money";
/** Net ROI math — v1's flipResult / brrrResult (permits-roi.js), transcribed
 * verbatim as pure functions. Same defaults (NR_MASTER_DEFAULTS), same
 * formulas, same edge behavior (∞ cash-on-cash when no cash left in deal).
 * Unit-tested against hand-computed vectors in tests/roi.test.ts. */

export interface NrInputs {
  arv: number;
  purchase: number;
  rehab: number;
  hold: number;
  ltv: number;
  rate: number;
  pts: number;
  svc: number;
  kiwi: number;
  buy: number;
  sell: number;
  insm: number;
  insmo: number;
  utilm: number;
  utilmo: number;
  staging: number;
  br_rent: number;
  br_refiltv: number;
  br_refirate: number;
  br_refiterm: number;
  br_vac: number;
  br_mgmt: number;
  br_maint: number;
  br_capex: number;
  br_taxes: number;
  br_ins: number;
  /** monthly HOA dues in $ (Tom 2026-08-19: "add the option to add HOA
   * for the BRRRR cash flow calculator"). Default 0 = no HOA, so every
   * existing saved master/per-property blob computes exactly as before. */
  br_hoa: number;
}

/** v1 NR_MASTER_DEFAULTS, verbatim (permits-roi.js:111). */
export const NR_DEFAULTS: NrInputs = {
  arv: 0, purchase: 0, rehab: 0,
  hold: 150, ltv: 90, rate: 10.7, pts: 1, svc: 999, kiwi: 1.5, buy: 2, sell: 4.5,
  insm: 5, insmo: 180, utilm: 5, utilmo: 180, staging: 2000,
  br_rent: 2200, br_refiltv: 75, br_refirate: 7, br_refiterm: 30,
  br_vac: 5, br_mgmt: 8, br_maint: 5, br_capex: 5, br_taxes: 3200, br_ins: 1800,
  br_hoa: 0,
};

export interface FlipResult {
  arv: number; purchase: number; loan: number; down: number; interest: number;
  orig: number; svc: number; buying: number; rehab: number; insurance: number;
  utilities: number; staging: number; selling: number; total: number;
  profit: number; invested: number; roi: number; coc: number; hold: number;
}

/** v1 flipResult(); cash mode zeroes the loan block like v1's nrMode==='cash'. */
export function flipResult(g: NrInputs, mode: "hard" | "cash" = "hard"): FlipResult {
  const { arv, purchase, rehab, hold } = g;
  const hard = mode !== "cash";
  const loan = hard ? (purchase * g.ltv) / 100 : 0;
  const down = purchase - loan;
  const interest = hard ? loan * (g.rate / 100) * (hold / 365) : 0;
  const orig = hard ? ((loan + rehab) * g.pts) / 100 : 0;
  const svc = hard ? g.svc : 0;
  const buying = (purchase * (g.buy + g.kiwi)) / 100;
  const insurance = g.insm * g.insmo;
  const utilities = g.utilm * g.utilmo;
  const staging = g.staging;
  const selling = (arv * g.sell) / 100;
  const total = purchase + interest + orig + svc + buying + rehab + insurance + utilities + staging + selling;
  const profit = arv - total;
  const invested = total - loan;
  const roi = total > 0 ? (profit / total) * 100 : 0;
  const coc = invested > 0 ? (profit / invested) * 100 : 0;
  return { arv, purchase, loan, down, interest, orig, svc, buying, rehab, insurance, utilities, staging, selling, total, profit, invested, roi, coc, hold };
}

export interface BrrrResult {
  arv: number; totalCashIn: number; refiLoan: number; cashLeft: number;
  cashOut: number; rent: number; noiY: number; mPmt: number; cfMo: number;
  cfY: number; capRate: number; coc: number; forcedEquity: number;
}

/** v1 brrrResult(), verbatim. */
export function brrrResult(g: NrInputs): BrrrResult {
  const { arv, purchase, rehab } = g;
  const buying = (purchase * (g.buy + g.kiwi)) / 100;
  const holding = g.insm * g.insmo + g.utilm * g.utilmo;
  const totalCashIn = purchase + rehab + buying + holding;
  const refiLoan = (arv * g.br_refiltv) / 100;
  const cashLeftRaw = totalCashIn - refiLoan;
  const cashLeft = Math.max(cashLeftRaw, 0);
  const cashOut = Math.max(-cashLeftRaw, 0);
  const rent = g.br_rent;
  const grossY = rent * 12;
  const effY = grossY * (1 - g.br_vac / 100);
  const opexY = (grossY * (g.br_mgmt + g.br_maint + g.br_capex)) / 100 + g.br_taxes + g.br_ins
    + (g.br_hoa || 0) * 12; // monthly HOA dues -> annual opex
  const noiY = effY - opexY;
  const im = g.br_refirate / 100 / 12;
  const nMo = (g.br_refiterm || 30) * 12;
  const mPmt = im > 0 ? (refiLoan * im) / (1 - Math.pow(1 + im, -nMo)) : refiLoan / nMo;
  const debtY = mPmt * 12;
  const cfY = noiY - debtY;
  const cfMo = cfY / 12;
  const capRate = arv > 0 ? (noiY / arv) * 100 : 0;
  // Tom 2026-08-11 ("cash on cash doesnt work"): $0 left in + NEGATIVE cash
  // flow rendered as a green "0.0%". With no cash left in, the ratio is
  // undefined — NaN here, an em-dash at every display site. Positive flow on
  // $0 in stays ∞ (infinite return), and only a true 0/0 shows 0.
  const coc = cashLeft > 0 ? (cfY / cashLeft) * 100
    : cfY > 0 ? Infinity : cfY < 0 ? NaN : 0;
  const forcedEquity = arv - totalCashIn;
  return { arv, totalCashIn, refiLoan, cashLeft, cashOut, rent, noiY, mPmt, cfMo, cfY, capRate, coc, forcedEquity };
}

/** v1 usd() / roiTierClass() — same tiers: >15 green, 10–15 amber, else red. */
export const usd = (n: number): string => moneyExactSigned(n);
export const roiTier = (pct: number): "good" | "ok" | "bad" =>
  pct > 15 ? "good" : pct >= 10 ? "ok" : "bad";

/** Merge NR_MASTER (from rec_org_settings) over defaults — junk-tolerant. */
export function mergeMaster(value: unknown): NrInputs {
  const out = { ...NR_DEFAULTS };
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    for (const k of Object.keys(NR_DEFAULTS) as (keyof NrInputs)[]) {
      const v = (value as Record<string, unknown>)[k];
      const n = typeof v === "number" ? v : typeof v === "string" ? parseFloat(v) : NaN;
      if (Number.isFinite(n)) out[k] = n;
    }
  }
  return out;
}
